import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { User, LogOut, KeyRound, Loader2, Eye, EyeOff } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ThemeSwitcher } from "@/components/ui/theme-switcher"
import { ZoomControl } from "@/components/ui/zoom-control"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/hooks/use-toast"
import apiClient from "@/api/client"
import { staffRoleLabel } from "@/lib/staff-roles"
import type { User as UserType } from "@/types"

interface UserMenuProps {
  user: UserType
  collapsed?: boolean
  size?: "sm" | "md" | "lg"
}

// Keep in sync with minPasswordLen in backend auth handler.
const MIN_PASSWORD_LEN = 8

export function UserMenu({ user, collapsed = false, size = "md" }: UserMenuProps) {
  const [changeOpen, setChangeOpen] = useState(false)

  const handleLogout = () => {
    apiClient.clearAuth()
    window.location.href = '/login'
  }

  const sizeClasses = {
    sm: {
      avatar: "w-6 h-6",
      icon: "w-3 h-3",
      text: "text-xs",
      name: "text-xs",
      email: "text-xs"
    },
    md: {
      avatar: "w-8 h-8",
      icon: "w-4 h-4",
      text: "text-sm",
      name: "text-sm",
      email: "text-xs"
    },
    lg: {
      avatar: "w-10 h-10",
      icon: "w-5 h-5",
      text: "text-base",
      name: "text-base",
      email: "text-sm"
    }
  }

  const currentSize = sizeClasses[size]

  // Menu content is rendered twice (collapsed vs expanded trigger) — keep
  // the dropdown items in one place so the two branches can't drift.
  const menuItems = (
    <>
      <div className="px-2 py-1.5">
        <p className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Appearance
        </p>
        <ThemeSwitcher />
      </div>
      <ZoomControl />
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => setChangeOpen(true)}>
        <KeyRound className="mr-2 h-4 w-4" />
        <span>Change password</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
        <LogOut className="mr-2 h-4 w-4" />
        <span>Log out</span>
      </DropdownMenuItem>
    </>
  )

  return (
    <>
      {collapsed ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-auto p-1.5 rounded-full">
              <div className={`bg-primary rounded-full flex items-center justify-center ${currentSize.avatar}`}>
                <User className={`text-primary-foreground ${currentSize.icon}`} />
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            side="right"
            // z-[70]: the narrow-viewport nav drawer is z-[55] and the mobile
            // header z-[60]; the menu portals to <body>, so without this it
            // renders *behind* the drawer on tablet/phone and looks like the
            // trigger does nothing. max-h keeps the tall menu scrollable on
            // short landscape tablets.
            className="z-[70] w-64 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-[15px] font-medium leading-none">{user.first_name} {user.last_name}</p>
                <p className="text-[13px] leading-none text-muted-foreground">{user.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start p-3 h-auto bg-muted/30 rounded-lg hover:bg-muted hover:text-foreground transition-colors min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                <div className={`bg-primary rounded-full flex items-center justify-center shrink-0 ${currentSize.avatar}`}>
                  <User className={`text-primary-foreground ${currentSize.icon}`} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className={`font-medium truncate ${currentSize.name}`}>
                    {user.first_name} {user.last_name}
                  </p>
                  <p className={`text-muted-foreground truncate ${currentSize.email}`}>
                    {staffRoleLabel(user.role)}
                  </p>
                </div>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            // See note above — must sit above the z-[55] drawer / z-[60]
            // mobile header so the menu is visible on tablet/phone.
            className="z-[70] w-64 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-[15px] font-medium leading-none">{user.first_name} {user.last_name}</p>
                <p className="text-[13px] leading-none text-muted-foreground">{user.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ChangePasswordDialog open={changeOpen} onOpenChange={setChangeOpen} />
    </>
  )
}

// A labelled password input with its own show/hide eye toggle. One component
// for every password field so the reveal control, a11y, and styling can never
// drift between fields. Reveal state is intentionally local — the dialog
// unmounts on close, so each open starts hidden again.
function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  disabled,
  autoFocus,
  hint,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  autoComplete: string
  disabled?: boolean
  autoFocus?: boolean
  hint?: string
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={disabled}
          className="pr-9"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          disabled={disabled}
          aria-label={show ? "Hide password" : "Show password"}
          aria-pressed={show}
          // Keep out of the tab order so Tab moves field → field, not field → eye.
          tabIndex={-1}
          className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

// Inline modal component — small enough that the extra file wouldn't pay for
// itself, tightly coupled to the menu for discoverability. If this grows we
// split it into components/forms/.
function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setCurrentPassword("")
    setNewPassword("")
    setConfirmPassword("")
    setError(null)
  }

  const mutation = useMutation({
    mutationFn: async (payload: { current: string; next: string }) =>
      apiClient.changePassword(payload.current, payload.next),
    onSuccess: () => {
      toast({
        title: "Password updated",
        description: "You'll keep your current session, but use the new password next time you sign in.",
      })
      reset()
      onOpenChange(false)
    },
    onError: (err: Error) => {
      setError(err.message || "Could not change password")
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!currentPassword) {
      setError("Please enter your current password")
      return
    }
    if (newPassword.length < MIN_PASSWORD_LEN) {
      setError(`New password must be at least ${MIN_PASSWORD_LEN} characters`)
      return
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords don't match")
      return
    }
    if (newPassword === currentPassword) {
      setError("New password must be different from your current one")
      return
    }
    mutation.mutate({ current: currentPassword, next: newPassword })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Always clear form when the dialog closes — stale passwords lying
        // around in component state is an easy way to leak them via React
        // devtools.
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      {/* z-[70]: opened from the user menu while the narrow nav drawer (z-[55])
          may still be open, so the dialog must outrank it to be usable on
          tablet/phone. Safe to bump in isolation — this dialog holds only
          inputs, no nested select/popover that could end up behind it. */}
      <DialogContent className="z-[70] sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="h-4 w-4 text-orange-600" />
            Change password
          </DialogTitle>
          <DialogDescription>
            Pick a new password for your account.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField
            id="current-password"
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            disabled={mutation.isPending}
            autoFocus
          />

          <PasswordField
            id="new-password"
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            disabled={mutation.isPending}
            hint={`At least ${MIN_PASSWORD_LEN} characters.`}
          />

          <PasswordField
            id="confirm-password"
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            disabled={mutation.isPending}
          />

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50/70 px-3 py-2 text-[13px] text-rose-800">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating…
                </span>
              ) : (
                "Update password"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
